// src/pages/TestResults.js
import React, { useState, useCallback, useRef } from "react";
import {
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
} from "@carbon/react";
import CustomButton from "../components/CustomButton";
import TestResultEditorModal from "../components/TestResultEditorModal";
import { useAuth } from "../auth/AuthContext";
import { useEffect } from "react";
import api from "../api";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function TestResults() {
  const { user, role } = useAuth() || {};
  const storedPatient = sessionStorage.getItem("patient");
  const patient = storedPatient ? JSON.parse(storedPatient) : null;
  const research_id = patient?.research_id || user?.id || "P010";

  // EP は編集不可（必要なら attending_physician などに絞ってもOK）
  const canEdit = role && role !== "expert_physician";
  const [histories, setHistories] = useState({});

  // --- 共通fetch関数 ---
  const fetchTestResults = useCallback(async () => {
    try {
      const res = await api.get(`/v1/doctor/pro_test/${research_id}`);
      const data = res.data;
      console.log("検査結果:", data);

      const sortByDateDesc = (arr) =>
        Array.isArray(arr)
          ? [...arr].sort((a, b) => new Date(b.dates) - new Date(a.dates))
          : [];
      const latest = (arr) => {
        const sorted = sortByDateDesc(arr);
        return sorted.length ? sorted[0] : { dates: "-", value: "-" };
      };

      setPrev({
        objective: {
          higherCognitive: {
            sdmt: { date: latest(data.sdmt).dates, correct: latest(data.sdmt).value },
            cogeval: { date: latest(data.cogeval).dates, z: latest(data.cogeval).value },
          },
          walking: {
            t25fw: { date: latest(data.t25fw).dates, sec: latest(data.t25fw).value },
          },
          upperLimb: {
            nineHpt: {
              date: latest(data.hpt9).dates,
              domSec: latest(data.hpt9).value,
              nonDomSec: latest(data.hpt9_vs).value,
            },
          },
        },
        pro: {
          higherCognitive: { msnq: { date: latest(data.msnq).dates, score: latest(data.msnq).value } },
          walking: { msws12: { date: latest(data.msws_12).dates, score: latest(data.msws_12).value } },
          upperLimb: { amsq: { date: latest(data.amsq).dates, score: latest(data.amsq).value } },
          fatigue: { mfis: { date: latest(data.mfis).dates, score: latest(data.mfis).value } },
        },
      });

      setHistories({
        SDMT: sortByDateDesc(data.sdmt)?.map((d) => ({ date: d.dates, value: d.value })) ?? [],
        CogEval: sortByDateDesc(data.cogeval)?.map((d) => ({ date: d.dates, value: `Z ${d.value}` })) ?? [],
        T25FW: sortByDateDesc(data.t25fw)?.map((d) => ({ date: d.dates, value: `${d.value} 秒` })) ?? [],
        "9HPT": sortByDateDesc(data.hpt9)?.map((d, i) => ({
          date: d.dates,
          value: `利き ${d.value} ／ 非利き ${sortByDateDesc(data.hpt9_vs)?.[i]?.value ?? "-"}`,
        })) ?? [],
        MSNQ: sortByDateDesc(data.msnq)?.map((d) => ({ date: d.dates, value: d.value })) ?? [],
        "MSWS-12": sortByDateDesc(data.msws_12)?.map((d) => ({ date: d.dates, value: d.value })) ?? [],
        AMSQ: sortByDateDesc(data.amsq)?.map((d) => ({ date: d.dates, value: d.value })) ?? [],
        MFIS: sortByDateDesc(data.mfis)?.map((d) => ({ date: d.dates, value: d.value })) ?? [],
      });
    } catch (err) {
      console.error("検査結果の取得に失敗:", err);
    }
  }, []);

  useEffect(() => {
    fetchTestResults();
  }, [fetchTestResults]);

  // ▼ APIで上書きする対象データ
  const [prev, setPrev] = useState({
    objective: {
      higherCognitive: {
        sdmt: { date: "-", correct: "-" },
        cogeval: { date: "-", z: "-" },
      },
      walking: { t25fw: { date: "-", sec: "-" } },
      upperLimb: {
        nineHpt: { date: "-", domSec: "-", nonDomSec: "-" },
      },
    },
    pro: {
      higherCognitive: { msnq: { date: "-", score: "-" } },
      walking: { msws12: { date: "-", score: "-" } },
      upperLimb: { amsq: { date: "-", score: "-" } },
      fatigue: { mfis: { date: "-", score: "-" } },
    },
  });

  const columns = ["SDMT","CogEval","T25FW","9HPT","MSWS-12","AMSQ","MFIS","MSNQ"];
  const headers = columns.map((key) => ({ key, header: key }));
  const maxLen = Math.max(...columns.map((key) => (histories[key]?.length || 0)));
  const fmt = (iso) => (iso ? iso.slice(0, 10).replaceAll("-", "/") : "");
  const tableRows = Array.from({ length: maxLen }).map((_, i) => {
    const row = { id: `r${i}` };
    columns.forEach((key) => {
      const item = histories[key]?.[i];
      row[key] = item ? `${fmt(item.date)}　—　${item.value}` : "";
    });
    return row;
  });

  // ▼ 見た目
  const rowHeader = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "0.5rem",
    marginBottom: "0.25rem",
  };
  const rowWrap = { display: "flex", flexWrap: "wrap", gap: "1.5rem", borderBottom: "1px solid #e0e0e0", paddingBottom: "0.75rem", marginBottom: "1rem" };
  const col = { flex: "1 1 30%", minWidth: "260px" };
  const label = { fontSize: "0.85rem", color: "#666", marginBottom: "0.25rem" };
  const blockTitle = { marginTop: "2.5rem", marginBottom: "0.25rem", fontWeight: "bold", fontSize: "1.2rem" };
  const subtle = { fontSize: "0.9rem", color: "#333" };

  // ▼ 編集モーダル制御
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [editingInitial, setEditingInitial] = useState(null);

  // 同じモーダルでも毎回確実に“別インスタンス”にするための連番
  const modalSeqRef = useRef(0);
  const bumpModalSeq = () => { modalSeqRef.current += 1; };

  // 画面全体の flatpickr/DatePicker を強制クローズ + フォーカス掃除
  const closeAllCalendars = useCallback(() => {
    try {
      document.querySelectorAll(".flatpickr-input").forEach((inp) => {
        const fp = inp._flatpickr;
        if (fp?.isOpen) fp.close();
      });
    } catch {}
    try {
      document.activeElement?.blur?.();
    } catch {}
  }, []);

  // モーダルを安全に閉じる共通関数（X/キャンセル/保存で共通）
  const closeEditor = useCallback(() => {
    closeAllCalendars();
    setEditingKey(null);
    setEditingInitial(null);
    setEditorOpen(false);
  }, [closeAllCalendars]);

  // 開く時のガード（EP は開けない）
  const openEditor = (testKey) => {
    if (!canEdit) return; // ここでブロック
    closeAllCalendars();

    const pick = (k) => {
      switch (k) {
        case "sdmt": return prev.objective.higherCognitive.sdmt;
        case "cogeval": return prev.objective.higherCognitive.cogeval;
        case "t25fw": return prev.objective.walking.t25fw;
        case "nineHpt": return prev.objective.upperLimb.nineHpt;
        case "msnq": return prev.pro.higherCognitive.msnq;
        case "msws12": return prev.pro.walking.msws12;
        case "amsq": return prev.pro.upperLimb.amsq;
        case "mfis": return prev.pro.fatigue.mfis;
        default: return {};
      }
    };

    setEditorOpen(false);
    setEditingKey(testKey);
    setEditingInitial(pick(testKey));
    bumpModalSeq();
    requestAnimationFrame(() => setEditorOpen(true));
  };

  const applySave = (testKey, val) => {
    // 念のため保存もガード
    if (!canEdit) return;
    setPrev((p) => {
      const copy = structuredClone ? structuredClone(p) : JSON.parse(JSON.stringify(p));
      switch (testKey) {
        case "sdmt": copy.objective.higherCognitive.sdmt = val; break;
        case "cogeval": copy.objective.higherCognitive.cogeval = val; break;
        case "t25fw": copy.objective.walking.t25fw = val; break;
        case "nineHpt": copy.objective.upperLimb.nineHpt = val; break;
        case "msnq": copy.pro.higherCognitive.msnq = val; break;
        case "msws12": copy.pro.walking.msws12 = val; break;
        case "amsq": copy.pro.upperLimb.amsq = val; break;
        case "mfis": copy.pro.fatigue.mfis = val; break;
        default: break;
      }
      return copy;
    });
    console.log("saved:", testKey, val);
  };

  const fmtDate = (iso) => iso;

  // 編集ボタン（EP では非表示）
  const EditBtn = ({ onClick }) =>
    canEdit ? (
      <CustomButton onClick={onClick} style={{ marginLeft: 8 }} size="sm">
        編集
      </CustomButton>
    ) : null;

  return (
    <div style={{ maxWidth: 1000, margin: "1rem auto" }}>
      {/* タイトル */}
      <div style={{ display: "flex", alignItems: "center", marginBottom: "1rem" }}>
        <h1 style={{ margin: 0 }}>
          検査結果一覧：PRO・客観的指標
        </h1>
      </div>

      <h2>最新の結果</h2>

      {/* ===== 客観的指標 ===== */}
      <h3>客観的指標</h3>

      {/* 高次脳機能 */}
      <h4>高次脳機能</h4>
      <div style={rowWrap}>
        <div style={col}>
          <div style={rowHeader}>
            <div style={label}>SDMT（90秒 正答数）</div>
            <EditBtn onClick={() => openEditor("sdmt")} />
          </div>
          <div style={subtle}>
            {fmtDate(prev.objective.higherCognitive.sdmt.date)} ／ {prev.objective.higherCognitive.sdmt.correct} 問
          </div>
        </div>

        <div style={col}>
          <div style={rowHeader}>
            <div style={label}>CogEval（Zスコア）</div>
            <EditBtn onClick={() => openEditor("cogeval")} />
          </div>
          <div style={subtle}>
            {fmtDate(prev.objective.higherCognitive.cogeval.date)} ／ Z {prev.objective.higherCognitive.cogeval.z}
          </div>
        </div>
      </div>

      {/* 歩行機能 */}
      <h4>歩行機能</h4>
      <div style={rowWrap}>
        <div style={col}>
          <div style={rowHeader}>
            <div style={label}>T25FW（秒）</div>
            <EditBtn onClick={() => openEditor("t25fw")} />
          </div>
          <div style={subtle}>
            {fmtDate(prev.objective.walking.t25fw.date)} ／ {prev.objective.walking.t25fw.sec} 秒
          </div>
        </div>
      </div>

      {/* 上肢機能 */}
      <h4>上肢機能</h4>
      <div style={rowWrap}>
        <div style={col}>
          <div style={rowHeader}>
            <div style={label}>9HPT（秒）</div>
            <EditBtn onClick={() => openEditor("nineHpt")} />
          </div>
          <div style={subtle}>
            {fmtDate(prev.objective.upperLimb.nineHpt.date)}<br />
            利き手 {prev.objective.upperLimb.nineHpt.domSec} 秒 ／ 非利き手 {prev.objective.upperLimb.nineHpt.nonDomSec} 秒
          </div>
        </div>
      </div>

      {/* ===== PRO ===== */}
      <div style={blockTitle}>主観的指標 / PRO</div>

      {/* 歩行機能（MSWS-12） */}
      <h4>歩行機能</h4>
      <div style={rowWrap}>
        <div style={col}>
          <div style={rowHeader}>
            <div style={label}>MSWS-12（合計点）</div>
            <EditBtn onClick={() => openEditor("msws12")} />
          </div>
          <div style={subtle}>
            {fmtDate(prev.pro.walking.msws12.date)} ／ {prev.pro.walking.msws12.score}
          </div>
        </div>
      </div>

      {/* 上肢機能（AMSQ） */}
      <h4>上肢機能</h4>
      <div style={rowWrap}>
        <div style={col}>
          <div style={rowHeader}>
            <div style={label}>AMSQ（合計点）</div>
            <EditBtn onClick={() => openEditor("amsq")} />
          </div>
          <div style={subtle}>
            {fmtDate(prev.pro.upperLimb.amsq.date)} ／ {prev.pro.upperLimb.amsq.score}
          </div>
        </div>
      </div>

      {/* 疲労（MFIS） */}
      <h4>疲労</h4>
      <div style={rowWrap}>
        <div style={col}>
          <div style={rowHeader}>
            <div style={label}>MFIS（合計点）</div>
            <EditBtn onClick={() => openEditor("mfis")} />
          </div>
          <div style={subtle}>
            {fmtDate(prev.pro.fatigue.mfis.date)} ／ {prev.pro.fatigue.mfis.score}
          </div>
        </div>
      </div>

      {/* 高次脳機能（MSNQ） */}
      <h4>高次脳機能</h4>
      <div style={rowWrap}>
        <div style={col}>
          <div style={rowHeader}>
            <div style={label}>MSNQ（合計点）</div>
            <EditBtn onClick={() => openEditor("msnq")} />
          </div>
          <div style={subtle}>
            {fmtDate(prev.pro.higherCognitive.msnq.date)} ／ {prev.pro.higherCognitive.msnq.score}
          </div>
        </div>
      </div>

      {/* 履歴テーブル */}
      <h2 style={{ marginTop: "3rem" }}>検査結果履歴</h2>
      <div style={{ overflowX: "auto" }}>
        <DataTable rows={tableRows} headers={headers} size="sm">
          {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
            <TableContainer>
              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    {headers.map((header) => (
                      <TableHeader
                        key={header.key}
                        {...getHeaderProps({ header })}
                        style={{ position: "sticky", top: 0, zIndex: 1 }}
                      >
                        {header.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id} {...getRowProps({ row })}>
                      {row.cells.map((cell) => (
                        <TableCell key={cell.id} style={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
                          {cell.value || <span style={{ color: "#bbb" }}>—</span>}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataTable>
      </div>

      {/* エディタモーダル（EP では開かない & 保存も効かない：ダブルガード） */}
      <TestResultEditorModal
        key={`${editingKey ?? "closed"}-${modalSeqRef.current}`}
        open={Boolean(canEdit && editorOpen)}
        testKey={editingKey}
        initial={editingInitial}
        onClose={closeEditor}
        onSave={async (val) => {
          if (!canEdit) return;
          try {
            // --- 登録API呼び出し ---
            const answerDateJST = dayjs(val.date).tz("Asia/Tokyo").format("YYYY-MM-DD");
            await api.post("/v1/doctor/register_pro_test", {
              research_id,
              // answer_date: val.date.slice(0, 10),
              answer_date: answerDateJST,
              // answer_date: val.date,
              sdmt: editingKey === "sdmt" ? val.correct : null,
              cogeval: editingKey === "cogeval" ? val.z : null,
              t25fw: editingKey === "t25fw" ? val.sec : null,
              hpt9: editingKey === "nineHpt" ? val.domSec : null,
              hpt9_vs: editingKey === "nineHpt" ? val.nonDomSec : null,
              msnq: editingKey === "msnq" ? val.score : null,
              msws_12: editingKey === "msws12" ? val.score : null,
              amsq: editingKey === "amsq" ? val.score : null,
              mfis: editingKey === "mfis" ? val.score : null,
            });

            // --- 登録成功後に再取得 ---
            await fetchTestResults();
            console.log("再取得完了");

            // --- 表示更新＆モーダル閉じ ---
            applySave(editingKey, val);
            closeEditor();
          } catch (err) {
            console.error("登録APIに失敗:", err);
          }
        }}
      />
    </div>
  );
}
